import { Amount, Box, Text } from '@razorpay/blade/components';
import React from 'react';
import { GetWorkflowRequestDetailsContract } from 'schemas/GetWorkflowRequestDetails';
import { hasComponentAssignmentsData, transformEditPayrollRequestData } from '../EditPayroll/utils';
import { NewToOldAmountWithDelta } from './NewToOldAmountWithDelta';
import { OvertimeBreakdownTable } from './OvertimeBreakdownTable';
import { Divider } from '@razorpay/blade/components';
import { DetailsSkeleton } from 'components/ui/Skeletons/DetailsSkeleton';
import { useQuery } from 'react-query';
import api from 'api';
import type { SalaryComponent } from 'components/SalaryComponents/types';
import type { ComponentAssignment, ComponentAssignments } from '../EditPayroll/types';
import { useSalaryStructureComponents } from 'components/SalaryStructures/creation';
import { NewToOldAmount } from './NewToOldAmount';
import { useOvertimePreview } from '../EditPayroll/useOvertimePreview';

export interface EditPayrollDetailsProps {
  workflowRequestDetails?: GetWorkflowRequestDetailsContract;
  isLoading?: boolean;
}

const EditPayrollDetails = ({ workflowRequestDetails, isLoading }: EditPayrollDetailsProps) => {
  const requestData = workflowRequestDetails
    ? transformEditPayrollRequestData(
        workflowRequestDetails.workflow_requests_data,
        workflowRequestDetails.people_metadata,
      )
    : null;

  const isRearchRequest = requestData?.isRearchRequest;

  const componentAssignments = requestData?.componentAssignments;

  const isComponentAssignmentsRequest =
    requestData != null && hasComponentAssignmentsData(componentAssignments);

  const { allComponents: earningComponents, isLoading: isFetchingEarnings } =
    useSalaryStructureComponents({
      enabled: isComponentAssignmentsRequest,
    });

  const { data: deductionComponents, isLoading: isFetchingDeductions } = useQuery({
    queryKey: ['salaryComponents', 'deductions'],
    queryFn: () =>
      api.salaryComponents.queries.listSalaryComponents<SalaryComponent>({
        category: 'DEDUCTIONS_CATEGORY',
        status: 'COMPONENT_STATUS_ACTIVE',
      }),
    select: (data) => data?.payrollListSalaryComponents?.data?.salaryComponents ?? [],
    enabled: isComponentAssignmentsRequest,
  });

  const overtimeEntries = requestData?.overtime.entries ?? [];
  const hasOvertimeEntries = overtimeEntries.length > 0;

  const { data: otFormulaCardNameById, isLoading: isFetchingOtCards } = useQuery({
    queryKey: ['overtime-formula-cards'],
    queryFn: () => api.overtime.queries.listFormulaCards({}),
    select: (response) => {
      const map = new Map<string, string>();
      (response?.overtimeListFormulaCards?.data?.otFormulaCards ?? []).forEach((card) => {
        map.set(card.id, card.name);
      });
      return map;
    },
    enabled: hasOvertimeEntries,
  });

  const {
    amountByCardId: overtimeAmountByCardId,
    total: overtimePreviewTotal,
    isLoading: isFetchingOvertimePreview,
  } = useOvertimePreview({
    entries: overtimeEntries,
    userId: requestData?.forPeopleId ?? null,
    payrollMonth: requestData?.overtimePayrollMonth ?? '',
    enabled: hasOvertimeEntries,
  });

  if (
    isLoading ||
    !workflowRequestDetails ||
    !requestData ||
    isFetchingEarnings ||
    isFetchingDeductions ||
    isFetchingOtCards ||
    isFetchingOvertimePreview
  ) {
    return <DetailsSkeleton rowCount={8} />;
  }

  if (isRearchRequest) {
    const componentNameById = new Map<string, string>();
    [...earningComponents, ...(deductionComponents ?? [])].forEach((component) => {
      componentNameById.set(component.id, component.name);
    });

    const details =
      componentAssignments && isComponentAssignmentsRequest
        ? buildComponentAssignmentDetails(componentAssignments, componentNameById)
        : [];

    return (
      <Box display="flex" flexDirection="column" gap="spacing.5">
        <RequestHeader />
        <DetailsRow label="Employee Details">
          <Box display="flex" flexDirection="column">
            <Text color="surface.text.gray.muted">{requestData.employeeDetails.name}</Text>
            <Text variant="caption" color="surface.text.gray.muted" size="small">
              {requestData.employeeDetails.employeeId}
            </Text>
          </Box>
        </DetailsRow>
        <DetailsRow label="Payroll Month">
          <Text color="surface.text.gray.muted">{requestData.payrollMonth}</Text>
        </DetailsRow>
        {details.map(({ label, rows }) => (
          <React.Fragment key={label}>
            <DetailsRow label={label}>
              <Box display="flex" flexDirection="column" gap="spacing.2">
                {rows.map((row, index) => (
                  <Box
                    key={`${row.name}-${index}`}
                    display="flex"
                    justifyContent="space-between"
                    gap="spacing.5">
                    <Text size="small" color="surface.text.gray.muted">
                      {row.name}
                    </Text>
                    {typeof row.content === 'string' ? (
                      <Text size="small" color="surface.text.gray.subtle">
                        {row.content}
                      </Text>
                    ) : (
                      row.content
                    )}
                  </Box>
                ))}
              </Box>
            </DetailsRow>
            <DetailsDivider />
          </React.Fragment>
        ))}
        {(requestData.arrears.new ?? 0) !== 0 && (
          <>
            <DetailsRow label="Arrears">
              <Box flexGrow="1">
                <NewToOldAmountWithDelta
                  newAmount={requestData.arrears.new ?? 0}
                  oldAmount={requestData.arrears.old ?? 0}
                />
              </Box>
            </DetailsRow>
            <DetailsDivider />
          </>
        )}
        {requestData.overtime.entries.length > 0 && (
          <>
            <DetailsRow label="Overtime">
              <Box flexGrow="1">
                <OvertimeBreakdownTable
                  entries={requestData.overtime.entries}
                  total={overtimePreviewTotal}
                  amountByCardId={overtimeAmountByCardId}
                  formulaCardNameById={otFormulaCardNameById}
                />
              </Box>
            </DetailsRow>
            <DetailsDivider />
          </>
        )}
      </Box>
    );
  }

  const details = [
    {
      label: 'Employee Details',
      content: (
        <Box display="flex" flexDirection="column">
          <Text color="surface.text.gray.muted">{requestData.employeeDetails.name}</Text>
          <Text variant="caption" color="surface.text.gray.muted" size="small">
            {requestData.employeeDetails.employeeId}
          </Text>
        </Box>
      ),
    },
    {
      label: 'Payroll Month',
      content: (
        <Box>
          <Text color="surface.text.gray.muted">{requestData.payrollMonth}</Text>
        </Box>
      ),
    },
    {
      label: 'Total Additions',
      content: (
        <Box flexGrow="1">
          <NewToOldAmountWithDelta
            newAmount={requestData.additions.newSum ?? 0}
            oldAmount={requestData.additions.oldSum ?? 0}
          />
        </Box>
      ),
      breakupComponents: Object.values(requestData.additions.new ?? {}).map((item) => ({
        componentName: item.name,
        content: <Amount value={item.amount as any as number} type="body" size="small" />,
      })),
    },
    {
      label: 'Total Deductions',
      content: (
        <Box flexGrow="1">
          <NewToOldAmountWithDelta
            newAmount={requestData.deductions.newSum ?? 0}
            oldAmount={requestData.deductions.oldSum ?? 0}
          />
        </Box>
      ),
      breakupComponents: (requestData.deductions.new ?? []).map((item) => ({
        componentName: item.name,
        content: <Amount value={item.amount as any as number} type="body" size="small" />,
      })),
    },
    {
      label: 'Arrears',
      content: (
        <NewToOldAmount newAmount={requestData.arrears.new} oldAmount={requestData.arrears.old} />
      ),
    },
  ];

  return (
    <Box display="flex" flexDirection="column" gap="spacing.5">
      <Box display="flex" gap="spacing.2" alignItems="baseline">
        <Text weight="semibold" size="large" color="surface.text.gray.subtle">
          Edit Payroll
        </Text>
        <Text size="large" color="surface.text.gray.muted">
          Details
        </Text>
      </Box>
      {details.map(({ label, content, breakupComponents }, index) => (
        <>
          <Box display="flex" flexDirection="column" gap="spacing.5">
            <Box display="flex" gap="spacing.5" alignItems="baseline" key={index}>
              <Box width="120px">
                <Text color="surface.text.gray.muted">{label}</Text>
              </Box>
              {content}
            </Box>
            {breakupComponents && breakupComponents?.length > 0 && (
              <Box>
                {breakupComponents?.map(({ componentName, content }) => (
                  <Box>
                    <Box display="flex" gap="spacing.5" alignItems="baseline">
                      <Box width="120px">
                        <Text size="small" color="surface.text.gray.muted">
                          {componentName}
                        </Text>
                      </Box>
                      <Box>{content}</Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box display="flex" width="100%" justifyContent="center" marginTop={'spacing.2'}>
            <Divider
              orientation="horizontal"
              dividerStyle="solid"
              alignSelf="center"
              variant="subtle"
              thickness="thick"
            />
          </Box>
        </>
      ))}
    </Box>
  );
};

const RequestHeader = () => (
  <Box display="flex" gap="spacing.2" alignItems="baseline">
    <Text weight="semibold" size="large" color="surface.text.gray.subtle">
      Edit Payroll
    </Text>
    <Text size="large" color="surface.text.gray.muted">
      Details
    </Text>
  </Box>
);

const DetailsRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box display="flex" gap="spacing.5" alignItems="baseline">
    <Box width="120px">
      <Text color="surface.text.gray.muted">{label}</Text>
    </Box>
    <Box flexGrow="1">{children}</Box>
  </Box>
);

const DetailsDivider = () => (
  <Box display="flex" width="100%" justifyContent="center" marginTop="spacing.2">
    <Divider
      orientation="horizontal"
      dividerStyle="solid"
      alignSelf="center"
      variant="subtle"
      thickness="thick"
    />
  </Box>
);

const toComponentRows = (
  assignments: ComponentAssignment[] | null | undefined,
  componentNameById: Map<string, string>,
): Array<{ name: string; content: React.ReactNode }> =>
  (assignments ?? []).map((assignment) => ({
    name: componentNameById.get(assignment.componentId) ?? assignment.componentId,
    // component assignment values are stored in paise — convert to rupees for display
    content: (
      <Amount
        value={Number(assignment.value) / 100}
        size="small"
        color="surface.text.gray.subtle"
      />
    ),
  }));

const buildComponentAssignmentDetails = (
  componentAssignments: ComponentAssignments,
  componentNameById: Map<string, string>,
): Array<{ label: string; rows: Array<{ name: string; content: React.ReactNode }> }> => {
  const details: Array<{ label: string; rows: Array<{ name: string; content: React.ReactNode }> }> =
    [];

  const additionRows = [
    ...toComponentRows(componentAssignments.addition, componentNameById),
    ...toComponentRows(componentAssignments.variablePay, componentNameById),
  ];
  if (additionRows.length > 0) {
    details.push({ label: 'Additions', rows: additionRows });
  }

  const deductionRows = toComponentRows(componentAssignments.deduction, componentNameById);
  if (deductionRows.length > 0) {
    details.push({ label: 'Deductions', rows: deductionRows });
  }

  if (componentAssignments.lop !== null && componentAssignments.lop !== undefined) {
    details.push({
      label: 'Loss of Pay',
      rows: [{ name: 'LOP Days', content: `${componentAssignments.lop}` }],
    });
  }

  return details;
};

export { EditPayrollDetails };
